import { format } from "date-fns";
import { CalendarPlus } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface AddToCalendarProps {
  title: string;
  date: Date;
  time: string;
  durationMinutes: number;
  location?: string;
}

const pad = (n: number) => n.toString().padStart(2, "0");

const toUTCString = (d: Date) =>
  `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00Z`;

const buildDates = (date: Date, time: string, durationMinutes: number) => {
  const [h, m] = time.split(":").map(Number);
  const start = new Date(date);
  start.setHours(h, m, 0, 0);
  const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
  return { start, end };
};

const googleCalendarUrl = (title: string, start: Date, end: Date, location?: string) => {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates: `${toUTCString(start)}/${toUTCString(end)}`,
  });
  if (location) params.set("location", location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const appleCalendarBlob = (title: string, start: Date, end: Date, location?: string) => {
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Slotify//Booking//EN",
    "BEGIN:VEVENT",
    `DTSTART:${toUTCString(start)}`,
    `DTEND:${toUTCString(end)}`,
    `SUMMARY:${title}`,
    ...(location ? [`LOCATION:${location}`] : []),
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  return new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" });
};

const AddToCalendar = ({ title, date, time, durationMinutes, location }: AddToCalendarProps) => {
  const { t } = useLanguage();
  const { start, end } = buildDates(date, time, durationMinutes);

  const handleApple = () => {
    const blob = appleCalendarBlob(title, start, end, location);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "appointment.ics";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mt-4">
      <a
        href={googleCalendarUrl(title, start, end, location)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
      >
        <CalendarPlus className="h-4 w-4" />
        {t("calendar.add_google")}
      </a>
      <button
        onClick={handleApple}
        className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
      >
        <CalendarPlus className="h-4 w-4" />
        {t("calendar.add_apple")}
      </button>
    </div>
  );
};

export default AddToCalendar;
